/*
 * Builds filter bar: pure model, no React. The search-param helpers (statuses, dirty flag, status toggle and summary) and
 * the quick-filter presets of screen 02.
 */
import type { BuildStatus } from '@hangar/shared';
import type { BuildsSearch } from '@/router';
import { ALL_STATUSES, STATUS_LABEL } from './buildHelpers';

/** The `status` search param (comma separated) as the known statuses it names. */
export const statusesOf = (s: string | undefined): BuildStatus[] =>
  s ? (s.split(',').filter((x): x is BuildStatus => (ALL_STATUSES as string[]).includes(x)) as BuildStatus[]) : [];

const RUNNING_STATUSES = 'running,verifying,dispatching,provider_queued';
const FAILED_STATUSES = 'failed,errored,timed_out';

export interface FilterPreset {
  key: string;
  label: string;
  testId: string;
  match: (s: BuildsSearch) => boolean;
  apply: (s: BuildsSearch) => BuildsSearch;
}

/** Quick filters of screen 02 (All · Running · Production · Failed) expressed as search presets. */
export const PRESETS: FilterPreset[] = [
  {
    key: 'all',
    label: 'All',
    testId: 'builds-preset-all',
    match: (s) => !s.status && !s.environment,
    apply: (s) => ({ ...s, status: undefined, environment: undefined }),
  },
  {
    key: 'live',
    label: 'Running',
    testId: 'builds-preset-running',
    match: (s) => s.status === RUNNING_STATUSES,
    apply: (s) => ({ ...s, status: RUNNING_STATUSES }),
  },
  {
    key: 'production',
    label: 'Production',
    testId: 'builds-preset-production',
    match: (s) => s.environment === 'production' && !s.status,
    apply: (s) => ({ ...s, environment: 'production', status: undefined }),
  },
  {
    key: 'failed',
    label: 'Failed',
    testId: 'builds-preset-failed',
    match: (s) => s.status === FAILED_STATUSES,
    apply: (s) => ({ ...s, status: FAILED_STATUSES }),
  },
];

const FILTER_KEYS = ['app', 'environment', 'platform', 'status', 'branch', 'q'] as const;

/** Whether any filter is set (the Reset button shows). */
export const isFiltered = (s: BuildsSearch): boolean => FILTER_KEYS.some((k) => Boolean(s[k]));

const SHEET_KEYS = ['app', 'environment', 'platform', 'status', 'branch'] as const;

/** How many filters of the phone sheet are set (the count on its "Filters" button; the search box sits outside). */
export const activeFilterCount = (s: BuildsSearch): number => SHEET_KEYS.filter((k) => Boolean(s[k])).length;

/** The `status` param after switching one status on or off; `undefined` when none is left. */
export function toggleStatus(statuses: BuildStatus[], status: BuildStatus, on: boolean): string | undefined {
  const next = on ? [...statuses, status] : statuses.filter((x) => x !== status);
  return next.length ? next.join(',') : undefined;
}

/** Text after "Status:" on the dropdown trigger: "all", the one status label or "<n> selected". */
export function statusSummary(statuses: BuildStatus[]): string {
  if (statuses.length === 0) return 'all';
  if (statuses.length === 1) return STATUS_LABEL[statuses[0]!];
  return `${statuses.length} selected`;
}
